import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  TagIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { useDropzone } from 'react-dropzone';
import Button from '../common/Button';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addUploadJob, updateUploadJob } from '../../store/slices/uploadSlice';
import { createCollaboration, updateCollaboration } from '../../store/slices/collaborationSlice';
import { userAPI, reviewsAPI, crmAPI, genericAPIs } from '../../services/api';
import axios from 'axios';
import toast from 'react-hot-toast';
import TagSelector from '../common/TagSelector';
import LocationPicker from '../common/LocationPicker';
import { Collaboration, UserSuggestion } from '../../types';

import ImageViewerModal from '../common/ImageViewerModal';
import Avatar from '../common/Avatar';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import { FaInstagram } from 'react-icons/fa';
import { compressImage } from '../../utils/compression';
import { getOptimizedImageUrl } from '../../utils/image';
import { saveDraft, loadDraft, clearDraft, hasDraft } from '../../utils/draftStorage';
import { Dialog } from '@headlessui/react';
import { useRestrictions } from '../../hooks/useRestrictions';
// [Subscription removed] UpgradePopup removed — platform is fully free.
// import UpgradePopup from '../common/UpgradePopup';

interface Collaborator {
  id: string;
  name: string;
  user_id?: string;
  profile_pic?: string;
  profile_pic_signed?: string;
  email?: string;
  role: string;
  is_portal_user: boolean;
  rating?: number;
  review?: string;
  is_locked?: boolean;
  is_existing_rating?: boolean;
}

const getProjectVideoThumbnail = (urlStr: string) => {
  try {
    const urlObj = new URL(urlStr);
    const host = urlObj.hostname.toLowerCase();
    const path = urlObj.pathname;
    if (host === 'youtu.be') return `https://img.youtube.com/vi/${path.slice(1)}/hqdefault.jpg`;
    if (host.includes('youtube.com')) {
      const v = urlObj.searchParams.get('v');
      if (v) return `https://img.youtube.com/vi/${v}/hqdefault.jpg`;
    }
    if (host.includes('drive.google.com')) {
      let fileId = "";
      const parts = path.split('/').filter(Boolean);
      for (let i = 0; i < parts.length; i++) {
        if (parts[i] === 'd' && i + 1 < parts.length) {
          fileId = parts[i+1];
          break;
        }
      }
      if (!fileId) {
        const idParam = urlObj.searchParams.get('id');
        if (idParam) fileId = idParam;
      }
      if (fileId) {
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w600`;
      }
    }
  } catch (e) {}
  return 'https://cdn-icons-png.flaticon.com/512/3767/3767084.png'; // Fallback folder icon
};

const isSameUrl = (url1: string, url2: string) => {
  const clean = (url: string) => {
    try {
      let path = url;
      if (path.includes('://')) {
        const u = new URL(path);
        path = u.pathname;
      }
      if (path.includes('uploads/')) {
        const idx = path.indexOf('uploads/');
        path = path.substring(idx);
      }
      const qIdx = path.indexOf('?');
      if (qIdx !== -1) {
        path = path.substring(0, qIdx);
      }
      return path.replace(/^\/+|\/+$/g, '');
    } catch(e) {
      return url;
    }
  };
  return clean(url1) === clean(url2);
};


const InstagramIcon = FaInstagram as any;

interface CreateCollaborationModalProps {
  mode?: 'create' | 'update';
  collaboration?: Collaboration; // Required for update mode
  collaborationRequest?: any;   // Link to a request for pre-filling
  instagramPreFill?: {
    title: string;
    project_description: string;
    tags: string[];
    images: string[];
    videos?: (string | { url: string; thumbnail?: string; rotation?: number })[];
    rotations?: Record<string, number>;
  };
  onClose: () => void;
  onSuccess?: (rewardData?: any) => void;
  onOpenSyncInstagram?: () => void;
}

const CreateCollaborationModal: React.FC<CreateCollaborationModalProps> = ({
  mode = 'create',
  collaboration,
  collaborationRequest,
  instagramPreFill,
  onClose,
  onSuccess,
  onOpenSyncInstagram
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  useLockBodyScroll();

  const isUpdateMode = mode === 'update';
  const modalTitle = isUpdateMode ? 'Update Project' : 'Create Project';
  const submitButtonText = isUpdateMode ? 'Update Project' : 'Create Project';

  const [crmProjectId, setCrmProjectId] = useState<string | null>(null);

  // Main form state
  const [formData, setFormData] = useState({
    title: collaboration?.title || collaborationRequest?.subject || instagramPreFill?.title || '',
    project_description: collaboration?.project_description || collaborationRequest?.message || instagramPreFill?.project_description || '',
    place: collaboration?.place || collaborationRequest?.location || '',
    is_paid: collaboration ? collaboration.is_paid : (collaborationRequest ? !!collaborationRequest.budget : true),
    budget: collaboration?.budget || collaborationRequest?.budget || '',
    duration: collaboration?.duration || collaborationRequest?.duration || '',
    start_date: collaboration?.start_date ? collaboration.start_date.split('T')[0] : (collaborationRequest?.preferred_dates?.[0]?.split('T')[0] || ''),
    end_date: collaboration?.end_date ? collaboration.end_date.split('T')[0] : '',
    creator_insights: collaboration?.creator_insights || '',
    creator_role: collaboration?.creator_role || (user?.user_type ? user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1) : ''),
  });

  // Tags
  const [tags, setTags] = useState<string[]>(
    collaboration?.tags ? collaboration.tags : (instagramPreFill?.tags ? instagramPreFill.tags : [])
  );

  // Collaborators
  const [collaborators, setCollaborators] = useState<Collaborator[]>(() => {
    if (isUpdateMode && collaboration?.collaborators) {
      return collaboration.collaborators
        .filter(c => c.user_id !== user?.id)
        .map((c, index) => ({
          id: c.id || `existing-${index}`,
          name: c.name,
          user_id: c.user_id,
          profile_pic: c.profile_pic_signed,
          email: c.email,
          role: c.role || '',
          is_portal_user: !!c.user_id,
          is_locked: c.is_locked
        }));
    }
    if (collaborationRequest) {
      const partner = collaborationRequest.sender.id === user?.id ? collaborationRequest.receiver : collaborationRequest.sender;
      return [{
        id: `request-${partner.id}`,
        name: `${partner.first_name} ${partner.last_name}`,
        user_id: partner.id,
        profile_pic: partner.profile_pic,
        profile_pic_signed: partner.profile_pic_signed,
        is_portal_user: true,
        role: '', // leave empty as requested
        is_locked: true
      }];
    }
    return [];
  });

  // User search
  const [userSuggestions, setUserSuggestions] = useState<{ [key: string]: UserSuggestion[] }>({});
  const [showSuggestions, setShowSuggestions] = useState<{ [key: string]: boolean }>({});

  // State
  const [existingReviews, setExistingReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const searchTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const suggestionsRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Click outside suggestions refs to close suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const updatedShowSuggestions = { ...showSuggestions };
      let changed = false;

      Object.keys(suggestionsRefs.current).forEach((id) => {
        const ref = suggestionsRefs.current[id];
        if (ref && !ref.contains(event.target as Node)) {
          if (showSuggestions[id]) {
            updatedShowSuggestions[id] = false;
            changed = true;
          }
        }
      });

      if (changed) {
        setShowSuggestions(updatedShowSuggestions);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Image Viewer
  const [viewingImageIndex, setViewingImageIndex] = useState<number | null>(null);

  // Draft Confirmation
  const [showDraftConfirm, setShowDraftConfirm] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);
  const [showReorderConfirm, setShowReorderConfirm] = useState(false);


  const {
      // checkRestriction,
      limits: tierLimits } = useRestrictions();
  // [Subscription removed] upgradeConfig state removed — no upgrade popups.
  //   const [upgradeConfig, setUpgradeConfig] = useState<{
  //       isOpen: boolean;
  //       featureName?: string;
  //       limit?: string;
  //       tier?: 'pro' | 'premium';
  //   }>({ isOpen: false });
  // Unified Gallery State
  interface GalleryItem {
    id: string;
    url: string;
    url_signed?: string;
    thumbnail?: string;
    rotation?: number;
    type: 'existing' | 'new' | 'instagram';
    mediaType: 'image' | 'video';
    file?: File;
  }

  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>(() => {
    const items: GalleryItem[] = [];
    
    if (instagramPreFill) {
      if (instagramPreFill.images) {
        instagramPreFill.images.forEach((imgUrl) => {
          const rot = instagramPreFill.rotations?.[imgUrl] || 0;
          items.push({
            id: imgUrl,
            url: imgUrl,
            url_signed: imgUrl,
            rotation: rot,
            type: 'instagram',
            mediaType: 'image'
          });
        });
      }
      if (instagramPreFill.videos) {
        instagramPreFill.videos.forEach((vidObj) => {
          const vidUrl = typeof vidObj === 'string' ? vidObj : vidObj.url;
          const thumbUrl = typeof vidObj === 'object' ? vidObj.thumbnail : undefined;
          const rot = typeof vidObj === 'object' ? (vidObj.rotation || 0) : (instagramPreFill.rotations?.[vidUrl] || 0);
          if (vidUrl) {
            items.push({
              id: vidUrl,
              url: vidUrl,
              url_signed: thumbUrl || vidUrl,
              thumbnail: thumbUrl,
              rotation: rot,
              type: 'instagram',
              mediaType: 'video'
            });
          }
        });
      }
      return items;
    }

    const imagesList = collaboration?.images || [];
    const videosList = collaboration?.videos || [];
    const orderList = collaboration?.media_order || [];

    if (orderList.length > 0) {
      orderList.forEach((mediaPathOrUrl) => {
        const isVideo = videosList.some(v => isSameUrl(v, mediaPathOrUrl));
        const isImage = imagesList.some(img => isSameUrl(img, mediaPathOrUrl));
        if (isVideo) {
          const vidUrl = videosList.find(v => isSameUrl(v, mediaPathOrUrl)) || mediaPathOrUrl;
          items.push({
            id: vidUrl,
            url: vidUrl,
            type: 'existing',
            mediaType: 'video'
          });
        } else if (isImage) {
          const imgUrl = imagesList.find(img => isSameUrl(img, mediaPathOrUrl)) || mediaPathOrUrl;
          const imgIdx = imagesList.findIndex(img => isSameUrl(img, mediaPathOrUrl));
          items.push({
            id: imgUrl,
            url: imgUrl,
            url_signed: collaboration?.thumbnail_images_signed?.[imgIdx],
            type: 'existing',
            mediaType: 'image'
          });
        }
      });
      // Fallback: Catch any images/videos that might not be in media_order
      imagesList.forEach((path, idx) => {
        if (!items.some(item => isSameUrl(item.url, path))) {
          items.push({
            id: path,
            url: path,
            url_signed: collaboration?.thumbnail_images_signed?.[idx],
            type: 'existing',
            mediaType: 'image'
          });
        }
      });
      videosList.forEach((path) => {
        if (!items.some(item => isSameUrl(item.url, path))) {
          items.push({
            id: path,
            url: path,
            type: 'existing',
            mediaType: 'video'
          });
        }
      });
    } else {
      // Default fallback: photos (images) first, then videos!
      imagesList.forEach((path, index) => {
        items.push({
          id: path,
          url: path,
          url_signed: collaboration?.thumbnail_images_signed?.[index],
          type: 'existing',
          mediaType: 'image'
        });
      });
      videosList.forEach((vid) => {
        items.push({
          id: vid,
          url: vid,
          type: 'existing',
          mediaType: 'video'
        });
      });
    }
    return items;
  });

  // Keep track of deleted existing images
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);

  // Videos state is derived from galleryItems to ensure single source of truth
  const videos = galleryItems.filter(item => item.mediaType === 'video').map(item => item.url);
  const [newVideoUrl, setNewVideoUrl] = useState('');

  const addVideo = () => {
    if (!newVideoUrl) {
      toast.error('Please paste a YouTube or Google Drive link first.');
      return;
    }
    const lowerUrl = newVideoUrl.toLowerCase();
    if (!lowerUrl.includes('youtube.com') && !lowerUrl.includes('youtu.be') && !lowerUrl.includes('drive.google.com')) {
      toast.error('Invalid URL. Please paste a YouTube or Google Drive link.');
      return;
    }
    setGalleryItems(prev => [...prev, {
      id: newVideoUrl,
      url: newVideoUrl,
      type: 'new',
      mediaType: 'video'
    }]);
    setNewVideoUrl('');
  };

  const removeVideo = (url: string) => {
    setGalleryItems(prev => prev.filter(item => item.url !== url));
  };

  // Google Drive project video upload states
  const [localVideoFile, setLocalVideoFile] = useState<File | null>(null);
  const [videoTab, setVideoTab] = useState<'link' | 'upload'>('link');
  const [driveConnected, setDriveConnected] = useState(true);
  const [driveAuthUrl, setDriveAuthUrl] = useState('');
  const [videoUploadProgress, setVideoUploadProgress] = useState<number | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  useEffect(() => {
    if (videoTab === 'upload') {
      checkDriveStatus();
    }
  }, [videoTab]);

  const checkDriveStatus = async () => {
    try {
      const res = await crmAPI.getGoogleDriveStatus();
      setDriveConnected(res.data?.connected || false);
      if (!res.data?.connected) {
        const authRes = await crmAPI.getGoogleDriveAuthURL();
        setDriveAuthUrl(authRes.data?.url || '');
      }
    } catch (e) {
      console.error('Failed to check Google Drive status', e);
    }
  };

  const handleLocalVideoUpload = async () => {
    if (!localVideoFile) {
      toast.error('Please select a video file first');
      return;
    }
    if (!driveConnected) {
      toast.error('Please connect Google Drive first');
      return;
    }

    setUploadingVideo(true);
    setVideoUploadProgress(0);

    try {
      // 1. Initiate Resumable Upload
      const initRes = await genericAPIs.initiateProjectVideoUpload({
        filename: localVideoFile.name,
        size: localVideoFile.size,
        content_type: localVideoFile.type || 'video/mp4'
      });

      const uploadUrl = initRes.data?.upload_url;
      if (!uploadUrl) {
        throw new Error("Failed to get Google Drive upload URL");
      }

      // 2. Perform Resumable Upload
      const cleanAxios = axios.create();
      const driveRes = await cleanAxios.put(uploadUrl, localVideoFile, {
        headers: {
          'Content-Type': localVideoFile.type || 'video/mp4',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setVideoUploadProgress(pct);
          }
        }
      });

      const fileId = driveRes.data?.id;
      if (!fileId) {
        throw new Error("Failed to retrieve Google Drive File ID");
      }

      // 3. Complete upload
      const completeRes = await genericAPIs.completeProjectVideoUpload({
        file_id: fileId,
        filename: localVideoFile.name
      });

      const driveLink = completeRes.data?.drive_link;
      if (!driveLink) {
        throw new Error("Failed to finalize video link");
      }

      // Append to gallery items
      setGalleryItems(prev => [...prev, {
        id: driveLink,
        url: driveLink,
        type: 'new',
        mediaType: 'video'
      }]);
      setLocalVideoFile(null);
      toast.success('Video uploaded to Google Drive & added to project successfully!');
    } catch (error: any) {
      console.error(error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to upload video';
      toast.error(errorMsg);
    } finally {
      setUploadingVideo(false);
      setVideoUploadProgress(null);
    }
  };
  
  // Reordering State
  const [isReordering, setIsReordering] = useState(false);
  const [reorderSelection, setReorderSelection] = useState<string[]>([]);
  const initialGalleryItems = useRef<GalleryItem[]>([]);

  const toggleReorder = () => {
    if (isReordering) {
        // "Done Reordering"
        finishReordering();
    } else {
        // Start Reordering
        initialGalleryItems.current = [...galleryItems]; // Save checkpoint
        setIsReordering(true);
        // Default: Select ALL items in current order
        setReorderSelection(galleryItems.map(item => item.id));
    }
  };

  const cancelReordering = () => {
    setGalleryItems(initialGalleryItems.current);
    setIsReordering(false);
    setReorderSelection([]);
  };

  const finishReordering = () => {
    if (!isReordering) return;

    // Apply sorting (based on current selection + unselected appended)
    const selectedItems = reorderSelection.map(id => galleryItems.find(item => item.id === id)).filter(Boolean) as GalleryItem[];
    const unselectedItems = galleryItems.filter(item => !reorderSelection.includes(item.id));
    
    setGalleryItems([...selectedItems, ...unselectedItems]);
    setIsReordering(false);
    setReorderSelection([]);
  };

  const handleImageClick = (id: string, index: number) => {
    if (isReordering) {
        setReorderSelection(prev => {
            if (prev.includes(id)) {
                // Deselect: remove and shift others up (handled automatically by array filter)
                return prev.filter(itemId => itemId !== id);
            } else {
                // Select: add to end
                return [...prev, id];
            }
        });
    } else {
        const item = galleryItems.find(i => i.id === id);
        if (item && item.mediaType === 'image') {
            const imageItems = galleryItems.filter(i => i.mediaType === 'image');
            const imgIndex = imageItems.findIndex(i => i.id === id);
            if (imgIndex !== -1) {
                setViewingImageIndex(imgIndex);
            }
        }
    }
  };

  const removeGalleryItem = (id: string) => {
    const item = galleryItems.find(i => i.id === id);
    if (item && item.type === 'existing') {
      setImagesToDelete(prev => [...prev, item.url]);
    }
    setGalleryItems(prev => prev.filter(i => i.id !== id));
    
    // Also remove from selection if reordering
    if (isReordering) {
        setReorderSelection(prev => prev.filter(itemId => itemId !== id));
    }
  };
  // Image Upload
  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 15,
    onDrop: async (acceptedFiles) => {
      const currentImagesCount = galleryItems.length;
      const maxAllowed = 15;
      
      if (currentImagesCount + acceptedFiles.length > maxAllowed) {
        // [Subscription removed] Max 15 images per collab post is a flat platform limit (no tier upsell)
        toast.error(`You can only upload up to ${maxAllowed} images per collaboration post.`);
        return;
      }

      const compressedFiles = await Promise.all(
        acceptedFiles.map(async (file: File) => {
          return await compressImage(file);
        })
      );

      const newItems: GalleryItem[] = [];
      for (const file of compressedFiles) {
        const reader = new FileReader();
        const promise = new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
        });
        reader.readAsDataURL(file);
        const url = await promise;
        
        newItems.push({
          id: `new-${Date.now()}-${Math.random()}`,
          url: url,
          type: 'new',
          mediaType: 'image' as const,
          file: file
        });
      }

      setGalleryItems(prev => [...prev, ...newItems]);
    },
  });

  // Submit Logic Update
  // ... (This will be handled in the handleSubmit update)

  const searchUsers = async (query: string, id: string) => {
    if (query.length < 2) {
      setUserSuggestions(prev => ({ ...prev, [id]: [] }));
      setShowSuggestions(prev => ({ ...prev, [id]: false }));
      return;
    }

    try {
      const response = await userAPI.searchUsers(query);
      setUserSuggestions(prev => ({
        ...prev,
        [id]: response.data.data || [],
      }));
      setShowSuggestions(prev => ({ ...prev, [id]: true }));
    } catch {
      setUserSuggestions(prev => ({ ...prev, [id]: [] }));
      setShowSuggestions(prev => ({ ...prev, [id]: false }));
    }
  };

  const debouncedSearchUsers = (name: string, id: string) => {
    if (searchTimeouts.current[id]) clearTimeout(searchTimeouts.current[id]);
    searchTimeouts.current[id] = setTimeout(() => searchUsers(name, id), 300);
  };

  const addCollaborator = () => {
    setCollaborators(prev => [
      ...prev,
      { id: Date.now().toString(), name: '', role: '', is_portal_user: false }
    ]);
  };

  const removeCollaborator = (id: string) =>
    setCollaborators(prev => prev.filter(c => c.id !== id));

  const updateCollaborator = (id: string, field: keyof Collaborator, value: string | boolean | number) => {
    setCollaborators(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    if (field === 'name') debouncedSearchUsers(value as string, id);
  };

  const selectUser = (collaboratorId: string, user: UserSuggestion) => {
    setCollaborators(prev => prev.map(c =>
      c.id === collaboratorId
        ? {
            ...c,
            name: `${user.full_name}`,
            user_id: user.id,
            profile_pic: user.profile_pic,
            profile_pic_signed: user.profile_pic_signed,
            is_portal_user: true,
            email: '',
            role: user.role
          }
        : c
    ));
    setShowSuggestions(prev => ({ ...prev, [collaboratorId]: false }));
  };

  // Fetch existing reviews in update mode to prevent duplicate ratings
  useEffect(() => {
    if (isUpdateMode && collaboration?.id) {
      reviewsAPI.getMyReviews({ booking_id: collaboration.id })
        .then(res => {
          if (res.data?.data?.reviews) {
            const reviews = res.data.data.reviews;
            setExistingReviews(reviews);
            // Sync with collaborators state to pre-fill ratings
            setCollaborators(prev => prev.map(c => {
               const r = reviews.find((rv: any) => rv.reviewee_id === c.user_id);
               if (r) {
                  return { ...c, rating: r.rating, review: r.comment, is_existing_rating: true };
               }
               return c;
            }));
          }
        })
        .catch(err => console.error('Failed to fetch existing reviews:', err));
    }
  }, [isUpdateMode, collaboration?.id]);

  // Check for draft on mount and check for crm project deep linking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const crmProjId = params.get('crm_project_id');
    
    if (crmProjId && !isUpdateMode) {
        setCrmProjectId(crmProjId);
        importFromCrmProject(crmProjId);
        return; // Don't load draft if we are importing from CRM
    }

    if (!isUpdateMode && hasDraft()) {
      const draft = loadDraft();
      if (draft) {
        const isEmpty = !formData.title && !formData.project_description;
        if (isEmpty) {
            setFormData(draft.formData);
            setTags(draft.tags);
            setCollaborators(draft.collaborators);
            
            // Restore images
            if (draft.images && draft.images.length > 0) {
                // Convert base64 back to gallery items
                const restoredItems: GalleryItem[] = [];
                
                Promise.all(draft.images.map(async (base64: string, idx: number) => {
                    const res = await fetch(base64);
                    const blob = await res.blob();
                    const file = new File([blob], `draft_image_${idx}.jpg`, { type: 'image/jpeg' });
                    
                    return {
                        id: `draft-${idx}-${Date.now()}`,
                        url: base64,
                        type: 'new' as const,
                        mediaType: 'image' as const,
                        file: file
                    };
                })).then(items => {
                    setGalleryItems(items);
                });
            }
            
            toast.success('Resumed from draft');
        }
      }
    }
  }, [isUpdateMode]);

  const importFromCrmProject = async (id: string) => {
      try {
          const { crmAPI } = await import('../../services/api');
          const res = await crmAPI.getProject(id);
          const project = res.data.data.project;
          
          if (project) {
              setFormData(prev => ({
                  ...prev,
                  title: project.title,
                  project_description: project.description || '',
                  place: project.location_name || project.location || '',
                  budget: project.total_amount?.toString() || '',
                  is_paid: project.total_amount > 0,
                  start_date: project.shoot_dates?.[0]?.start_date?.split('T')[0] || project.shoot_date?.split('T')[0] || ''
              }));
              
              // Load images from project gallery if published
              if (project.gallery_status === 'published' && project.images) {
                  const importedImages = project.images.map((img: any) => ({
                      id: img.url,
                      url: img.url,
                      url_signed: img.thumbnail_signed || img.url_signed, // Fallback to signed thumbnail if available
                      type: 'existing' as const,
                      mediaType: (img.media_type === 'video' ? 'video' : 'image') as 'video' | 'image'
                  }));
                  setGalleryItems(importedImages);
              }
              toast.success('Project details imported!');
          }
      } catch (err) {
          toast.error('Failed to import project details');
      }
  };

  const handleSaveDraft = () => {
    const imagesToSave = galleryItems
        .filter(item => item.type === 'new') // Only save new uploads
        .map(item => item.url);

    const success = saveDraft({
        formData,
        tags,
        collaborators,
        images: imagesToSave
    });
    if (success) {
        toast.success('Draft saved successfully');
        if (pendingClose) {
            onClose();
        }
        setShowDraftConfirm(false);
    } else {
        toast.error('Failed to save draft');
    }
  };

  const handleDiscard = () => {
    clearDraft();
    setShowDraftConfirm(false);
    if (pendingClose) {
        onClose();
    }
  };

  const handleCloseAttempt = () => {
    // Check if form is dirty (has any content)
    const hasContent = formData.title || formData.project_description || formData.place || collaborators.length > 0;
    
    if (hasContent && !isUpdateMode) {
        setPendingClose(true);
        setShowDraftConfirm(true);
    } else {
        onClose();
    }
  };

  // Submit
  const handleSaveClick = () => {
    if (isReordering) {
      setShowReorderConfirm(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async (overrideGalleryItems?: GalleryItem[]) => {
    const itemsToUse = overrideGalleryItems || galleryItems;
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) newErrors.title = 'Project title is required';
    if (!formData.project_description.trim()) newErrors.project_description = 'Description is required';
    if (!formData.place.trim()) newErrors.place = 'Location is required';
    if (!formData.creator_role.trim()) newErrors.creator_role = 'Your role is required';
    if (formData.is_paid && !formData.budget) newErrors.budget = 'Budget is required for paid projects';
    if (!formData.start_date) newErrors.start_date = 'Start date is required';
    if (!formData.end_date) newErrors.end_date = 'End date is required';
    if (formData.start_date && formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) {
      newErrors.end_date = 'End date cannot be before start date';
    }

    let hasCollabError = false;
    collaborators.forEach(c => {
      if (c.name.trim() && !c.role.trim()) {
        newErrors[`collaborator_${c.id}_role`] = 'Role is required when name is provided';
        hasCollabError = true;
      }
      if (!c.name.trim() && c.role.trim()) {
        newErrors[`collaborator_${c.id}_name`] = 'Name is required when role is provided';
        hasCollabError = true;
      }
      if (c.is_locked && !c.role.trim()) {
        newErrors[`collaborator_${c.id}_role`] = 'Role is required for this team member';
        hasCollabError = true;
      }
      if (c.is_locked && !c.name.trim()) {
        newErrors[`collaborator_${c.id}_name`] = 'Name is required for this team member';
        hasCollabError = true;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const missingFields = Object.keys(newErrors).map(key => {
        switch(key) {
          case 'title': return 'Title';
          case 'project_description': return 'Description';
          case 'place': return 'Location';
          case 'creator_role': return 'Your Role';
          case 'budget': return 'Budget';
          case 'start_date': return 'Start Date';
          case 'end_date': return 'End Date';
          default: return key.startsWith('collaborator_') ? 'Collaborators' : key;
        }
      }).join(', ');
      toast.error(`Please fix errors in: ${missingFields}`);
      return;
    }

    // Validate Gallery Images
    if (itemsToUse.length === 0) {
      toast.error('Please upload at least one photo to the project gallery.');
      return;
    }
    if (itemsToUse.length > 15) {
      toast.error('A maximum of 15 images are allowed per collaboration project.');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Basic fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'is_paid') submitData.append(key, String(value));
        else if (value) submitData.append(key, String(value));
      });

      if (tags.length > 0) submitData.append('tags', JSON.stringify(tags));

      // Collaborators
      const validCollaborators = collaborators
        .filter(c => c.name.trim() && c.role.trim())
        .map(({ id, ...rest }) => ({ ...rest }));

      // Add creator if not present
      const creatorId = user?.id;
      if (user && !validCollaborators.some(c => c.user_id === creatorId)) {
        validCollaborators.unshift({
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
          user_id: creatorId,
          profile_pic: user.profile_pic,
          email: user.email,
          role: formData.creator_role,
          is_portal_user: true
        });
      }
      if (validCollaborators.length > 0) submitData.append('collaborators', JSON.stringify(validCollaborators));
      
      if (crmProjectId) submitData.append('crm_project_id', crmProjectId);

      // Images
      if (isUpdateMode) {
        // Extract existing images in their new order
        const currentExistingImages = itemsToUse
          .filter(item => item.type === 'existing' && item.mediaType === 'image')
          .map(item => item.url);
          
        if (currentExistingImages.length > 0) submitData.append('existing_images', JSON.stringify(currentExistingImages));
        if (imagesToDelete.length > 0) submitData.append('images_to_delete', JSON.stringify(imagesToDelete));
      }

      // Append videos JSON array
      const videoUrls = itemsToUse
        .filter(item => item.mediaType === 'video')
        .map(item => item.url);
      submitData.append('videos', JSON.stringify(videoUrls));

      // Append media_order JSON array
      const mediaOrder = itemsToUse.map(item => {
        if (item.type === 'new' && item.mediaType === 'image' && item.file) {
          return item.file.name;
        }
        return item.url;
      });
      submitData.append('media_order', JSON.stringify(mediaOrder));
      
      const instagramUrls = itemsToUse
        .filter(item => item.type === 'instagram' && item.mediaType === 'image')
        .map(item => item.url);
      if (instagramUrls.length > 0) {
        submitData.append('instagram_image_urls', JSON.stringify(instagramUrls));
      }

      // Collect rotations for images/videos
      const imageRotations: Record<string, number> = {};
      itemsToUse.forEach(item => {
        if (item.rotation) {
          imageRotations[item.url] = item.rotation;
        }
      });
      if (Object.keys(imageRotations).length > 0) {
        submitData.append('image_rotations', JSON.stringify(imageRotations));
      }

      const newFiles = itemsToUse
        .filter(item => item.type === 'new' && item.mediaType === 'image' && item.file)
        .map(item => item.file!);
        
      newFiles.forEach(file => submitData.append('images', file));

      const jobId = `collab-${Date.now()}`;
      const firstItem = itemsToUse.find(item => item.url);
      const firstImageUrl = firstItem 
        ? (firstItem.type === 'existing' 
            ? (firstItem.url_signed || getOptimizedImageUrl(firstItem.url, 'thumbnail')) 
            : firstItem.url) 
        : undefined;


      // Create background job
      dispatch(addUploadJob({
        id: jobId,
        title: formData.title.trim() || 'Collaboration',
        type: 'collaboration',
        status: 'uploading',
        progress: 0,
        thumbnailUrl: firstImageUrl || undefined
      }));

      const progressCallback = (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(updateUploadJob({ id: jobId, progress: percentCompleted }));
          if (percentCompleted === 100) {
            dispatch(updateUploadJob({ id: jobId, status: 'processing' }));
          }
        }
      };

      const operation = isUpdateMode && collaboration
        ? dispatch(updateCollaboration({ id: collaboration.id, formData: submitData, onUploadProgress: progressCallback }))
        : dispatch(createCollaboration({ formData: submitData, onUploadProgress: progressCallback }));

      // Process complete workflow in background
      operation.unwrap()
      .then(async (payload: any) => {
        dispatch(updateUploadJob({ id: jobId, status: 'completed' }));
        const collabId = isUpdateMode ? collaboration!.id : payload.id;

        const reviewsToSubmit = collaborators
          .filter(c => c.is_portal_user && c.rating && c.rating > 0 && c.user_id)
          .map(c => ({
            reviewee_id: c.user_id!,
            booking_id: collabId,
            rating: c.rating!,
            comment: c.review || ''
          }));

        if (reviewsToSubmit.length > 0) {
          try {
            await reviewsAPI.createReview(reviewsToSubmit);
          } catch (error) {
            console.error('Failed to submit reviews:', error);
          }
        }

        if (onSuccess) {
          const rewardData = payload?.__reward_unlocked;
          onSuccess(rewardData || undefined);
        }
      })
      .catch((error) => {
        dispatch(updateUploadJob({ id: jobId, status: 'error', error: typeof error === 'string' ? error : 'Upload failed' }));
      });

      // Instantly clear draft & close modal so user is free
      if (!isUpdateMode) clearDraft();
      
      if (crmProjectId) {
          const url = new URL(window.location.href);
          url.searchParams.delete('action');
          url.searchParams.delete('crm_project_id');
          window.history.replaceState({}, '', url.toString());
      }
      
      onClose();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Something went wrong');
    }
  };
  return ReactDOM.createPortal(
    <>
      <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[105] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-gray-900 z-10">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {modalTitle}
            </h2>
            <button
              onClick={handleCloseAttempt}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Source Tabs */}
          {onOpenSyncInstagram && !isUpdateMode && (
            <div className="grid grid-cols-2 border-b border-gray-200 dark:border-gray-800 bg-gray-100/80 dark:bg-gray-800/50 p-2 gap-2 shrink-0">
              <button
                type="button"
                className="py-2.5 px-4 rounded-xl text-xs md:text-sm font-semibold transition-all flex items-center justify-center space-x-2 bg-white dark:bg-gray-900 text-teal-600 dark:text-teal-400 shadow-sm border border-gray-200/60 dark:border-gray-700"
              >
                <PlusIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                <span>Create Manually</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleCloseAttempt();
                  onOpenSyncInstagram();
                }}
                className="py-2.5 px-4 rounded-xl text-xs md:text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-pink-600 dark:hover:text-pink-400 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all flex items-center justify-center space-x-2"
              >
                <InstagramIcon className="w-4 h-4 text-pink-500" />
                <span>Sync from Instagram</span>
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: Project Details */}
              <div className="lg:col-span-2 space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Project Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        setFormData({ ...formData, title: e.target.value });
                        if (errors.title) setErrors({ ...errors, title: '' });
                      }}
                      className={`w-full rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-2.5 focus:ring-teal-500 focus:border-teal-500 ${errors.title ? 'border-red-500' : ''}`}
                      placeholder="e.g. Summer Fashion Shoot"
                    />
                    {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.project_description}
                      onChange={(e) => {
                        setFormData({ ...formData, project_description: e.target.value });
                        if (errors.project_description) setErrors({ ...errors, project_description: '' });
                      }}
                      rows={4}
                      className={`w-full rounded-xl border-gray-200 px-4 py-2.5 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 focus:ring-teal-500 focus:border-teal-500 ${errors.project_description ? 'border-red-500' : ''}`}
                      placeholder="Describe the project, concept, and requirements..."
                    />
                    {errors.project_description && <p className="text-xs text-red-500 mt-1">{errors.project_description}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <MapPinIcon className="w-4 h-4 inline mr-1 text-gray-400" /> Location *
                      </label>
                      <div className={errors.place ? 'ring-1 ring-red-500 rounded-lg' : ''}>
                        <LocationPicker
                            value={formData.place}
                            onChange={(loc) => {
                              setFormData(f => ({ ...f, place: loc }));
                              if (errors.place) setErrors(prev => ({ ...prev, place: '' }));
                            }}
                            placeholder="City, Country"
                            className="w-full"
                        />
                      </div>
                      {errors.place && <p className="text-xs text-red-500 mt-1">{errors.place}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tags
                      </label>
                      <TagSelector
                        selectedTags={tags}
                        onChange={setTags}
                        placeholder="Add tags..."
                      />
                    </div>
                  </div>
                </div>

                  {/* Gallery */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <PhotoIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                          Project Gallery
                      </h3>
                      {galleryItems.length > 0 && (
                          <button
                            type="button"
                            onClick={toggleReorder}
                            className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                                isReordering 
                                ? 'bg-teal-600 text-white hover:bg-teal-700' 
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {isReordering ? 'Done Reordering' : 'Reorder Images'}
                          </button>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {galleryItems.map((item, index) => {
                            const selectionIndex = reorderSelection.indexOf(item.id);
                            const isSelected = selectionIndex !== -1;

                            return (
                                <div 
                                    key={item.id} 
                                    className={`relative aspect-square rounded-xl overflow-hidden group shadow-sm transition-all cursor-pointer ${
                                        isReordering 
                                            ? (isSelected ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-gray-900' : 'opacity-70 hover:opacity-100') 
                                            : 'bg-gray-100 dark:bg-gray-800'
                                    }`}
                                    onClick={() => handleImageClick(item.id, index)}
                                >
                                    {item.mediaType === 'video' ? (
                                        <div className="relative w-full h-full bg-black flex items-center justify-center overflow-hidden">
                                            <img 
                                                src={item.thumbnail || item.url_signed || getProjectVideoThumbnail(item.url)} 
                                                alt="Video Thumbnail" 
                                                className="w-full h-full object-cover opacity-80" 
                                                style={{ transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined }}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    const fallback = getProjectVideoThumbnail(item.url);
                                                    if (target.src !== fallback) {
                                                        target.src = fallback;
                                                    } else {
                                                        target.style.display = 'none';
                                                    }
                                                }}
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                                                <svg className="w-8 h-8 text-white drop-shadow-md" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z"/>
                                                </svg>
                                            </div>
                                        </div>
                                    ) : (
                                        <img 
                                            src={item.type === 'existing' ? (item.url_signed || getOptimizedImageUrl(item.url, 'thumbnail')) : item.url} 
                                            alt="" 
                                            className="w-full h-full object-cover" 
                                            style={{ transform: item.rotation ? `rotate(${item.rotation}deg)` : undefined }}
                                        />
                                    )}
                                    
                                    {/* Delete Button (Always visible on hover, or always if reordering) */}
                                    <div className={`absolute inset-0 transition-colors ${!isReordering ? 'bg-black/0 group-hover:bg-black/10' : ''}`}>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeGalleryItem(item.id);
                                            }}
                                            className="absolute top-1 right-1 bg-white/90 p-1 rounded-full text-gray-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                            onPointerDown={(e) => e.stopPropagation()}
                                        >
                                            <XMarkIcon className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Selection Badge (Reordering Only) */}
                                    {isReordering && isSelected && (
                                        <div className="absolute top-2 left-2 bg-teal-500 text-white text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full shadow-md z-10">
                                            {selectionIndex + 1}
                                        </div>
                                    )}

                                    {/* Standard Index Badge (View Only) */}
                                    {!isReordering && (
                                        <div className="absolute bottom-1 left-1 bg-black/50 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md backdrop-blur-sm">
                                            {index + 1}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Add Photo Button (Hidden while reordering to avoid confusion, or disable it) */}
                        {!isReordering && (
                            <div 
                            {...getRootProps()} 
                            className="aspect-square rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-teal-500 dark:hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/10 transition-all cursor-pointer flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 hover:text-teal-600 dark:hover:text-teal-400"
                            >
                            <input {...getInputProps()} />
                            <PlusIcon className="w-8 h-8 mb-1" />
                            <span className="text-[10px] font-medium">Add Photo</span>
                            </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Project Videos Section */}
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5-6 3.75v-7.5l6 3.75Z" />
                        </svg>
                        Project Videos
                      </h3>
                    </div>
                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
                      <button
                        type="button"
                        className={`flex-1 pb-2 text-xs font-semibold border-b-2 text-center transition-colors focus:outline-none ${
                          videoTab === 'link'
                            ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                        }`}
                        onClick={() => setVideoTab('link')}
                      >
                        Paste YouTube/Drive Link
                      </button>
                      <button
                        type="button"
                        className={`flex-1 pb-2 text-xs font-semibold border-b-2 text-center transition-colors focus:outline-none ${
                          videoTab === 'upload'
                            ? 'border-teal-600 text-teal-600 dark:border-teal-400 dark:text-teal-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                        }`}
                        onClick={() => setVideoTab('upload')}
                      >
                        Upload Video File
                      </button>
                    </div>

                    {videoTab === 'link' ? (
                      <div>
                        <div className="flex gap-2 mb-4">
                          <input
                            type="text"
                            placeholder="Paste YouTube or Google Drive URL"
                            value={newVideoUrl}
                            onChange={(e) => setNewVideoUrl(e.target.value)}
                            className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:border-teal-500 focus:ring-0 sm:text-sm"
                          />
                          <button
                            type="button"
                            onClick={addVideo}
                            className="bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap"
                          >
                            Add Link
                          </button>
                        </div>

                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-xl border border-blue-200 dark:border-blue-800/60 flex items-start gap-2.5">
                          <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-blue-800 dark:text-blue-200">
                              Google Drive Storage Integration
                            </p>
                            <p className="text-[10px] md:text-[11px] text-blue-700 dark:text-blue-300 mt-0.5 leading-relaxed">
                              To add local videos, upload them to your Google Drive first (e.g. inside the **"ClickCollab Projects"** folder) and paste the link here. Make sure the file sharing settings are set to "Anyone with the link can view".
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 mb-4">
                        {!driveConnected ? (
                          <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-200 dark:border-amber-800/50 flex flex-col items-center text-center">
                            <p className="text-sm text-amber-800 dark:text-amber-200 font-semibold mb-2">Google Drive Not Connected</p>
                            <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">You need to connect Google Drive to upload videos directly from your device.</p>
                            <a
                              href={driveAuthUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
                            >
                              🔗 Connect Google Drive
                            </a>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-xl border border-blue-200 dark:border-blue-800/60 flex items-start gap-2.5">
                              <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-bold text-blue-800 dark:text-blue-200">
                                  Google Drive Storage Integration
                                </p>
                                <p className="text-[10px] md:text-[11px] text-blue-700 dark:text-blue-300 mt-0.5 leading-relaxed">
                                  Local videos are uploaded directly to your own Google Drive and saved in the **"ClickCollab Projects"** folder.
                                </p>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Select Video File</label>
                              <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-teal-500 rounded-xl p-6 flex flex-col items-center justify-center hover:bg-teal-50/10 transition-colors cursor-pointer relative">
                                <input
                                  type="file"
                                  accept="video/*"
                                  onChange={(e) => {
                                    const selected = e.target.files?.[0];
                                    if (selected) {
                                      setLocalVideoFile(selected);
                                    }
                                  }}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  disabled={uploadingVideo}
                                />
                                <ArrowUpTrayIcon className="w-8 h-8 text-gray-400 mb-2" />
                                {localVideoFile ? (
                                  <div className="text-center">
                                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate max-w-[200px]">{localVideoFile.name}</p>
                                    <p className="text-[10px] text-gray-500">{(localVideoFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-500 text-center">Drag & drop or click to choose video file</p>
                                )}
                              </div>
                            </div>

                            {videoUploadProgress !== null && (
                              <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-gray-500">
                                  <span>Uploading...</span>
                                  <span>{videoUploadProgress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
                                  <div className="bg-teal-600 h-1.5 transition-all duration-150" style={{ width: `${videoUploadProgress}%` }} />
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={handleLocalVideoUpload}
                              disabled={uploadingVideo || !localVideoFile}
                              className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium text-xs py-2.5 rounded-lg transition-colors whitespace-nowrap"
                            >
                              {uploadingVideo ? 'Uploading...' : 'Upload & Add Video'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {videos.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4 bg-white dark:bg-gray-800/20 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                        No videos added to this project yet.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {videos.map((vidUrl, index) => {
                          let displayHost = "Link";
                          if (vidUrl.includes("youtube.com") || vidUrl.includes("youtu.be")) displayHost = "YouTube";
                          else if (vidUrl.includes("drive.google.com")) displayHost = "Google Drive";
                          else if (vidUrl.includes("vimeo.com")) displayHost = "Vimeo";
                          else if (vidUrl.includes("instagram.com")) displayHost = "Instagram Reels";

                          return (
                            <div key={index} className="flex justify-between items-center bg-white dark:bg-gray-800 px-3 py-2 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                              <div className="flex items-center gap-2 overflow-hidden mr-2">
                                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                                  displayHost === "YouTube" ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400" :
                                  displayHost === "Google Drive" ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400" :
                                  displayHost === "Vimeo" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400" :
                                  "bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400"
                                }`}>
                                  {displayHost}
                                </span>
                                <span className="text-xs text-gray-600 dark:text-gray-300 truncate font-mono">
                                  {vidUrl}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeVideo(vidUrl)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

              {/* Right Column: Logistics & Team */}
              <div className="space-y-6">
                
                {/* Schedule & Budget Card */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <CalendarDaysIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    Schedule & Budget
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Start Date</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => {
                          setFormData(f => ({ ...f, start_date: e.target.value }));
                          if (errors.start_date) setErrors(prev => ({ ...prev, start_date: '' }));
                        }}
                        className={`block w-full rounded-lg border bg-white dark:bg-gray-800 px-3 py-2.5 text-gray-900 dark:text-white focus:border-teal-500 focus:ring-teal-500 sm:text-sm ${
                          errors.start_date ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      />
                      {errors.start_date && <p className="text-xs text-red-500 mt-1">{errors.start_date}</p>}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">End Date</label>
                      <input
                        type="date"
                        min={formData.start_date}
                        value={formData.end_date}
                        onChange={(e) => {
                          setFormData(f => ({ ...f, end_date: e.target.value }));
                          if (errors.end_date) setErrors(prev => ({ ...prev, end_date: '' }));
                        }}
                        className={`block w-full rounded-lg border bg-white dark:bg-gray-800 px-3 py-2.5 text-gray-900 dark:text-white focus:border-teal-500 focus:ring-teal-500 sm:text-sm ${
                          errors.end_date ? 'border-red-500 focus:ring-red-500' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      />
                      {errors.end_date && <p className="text-xs text-red-500 mt-1">{errors.end_date}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Duration</label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={(e) => setFormData(f => ({ ...f, duration: e.target.value }))}
                      className="w-full border-gray-200 dark:border-gray-700 rounded-lg text-sm py-2.5 px-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="e.g. 2 days, 1 week"
                    />
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!formData.is_paid}
                          onChange={(e) => setFormData(f => ({ ...f, is_paid: !e.target.checked }))}
                          className="rounded text-teal-600 focus:ring-teal-500"
                        />
                        <CurrencyRupeeIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        Unpaid Project
                      </label>
                    </div>
                    
                    {formData.is_paid && (
                      <div className="animate-fadeIn">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Budget</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                          <input
                            type="number"
                            value={formData.budget}
                            onChange={(e) => {
                              setFormData(f => ({ ...f, budget: e.target.value }));
                              if (errors.budget) setErrors(prev => ({ ...prev, budget: '' }));
                            }}
                            className={`w-full border-gray-200 dark:border-gray-700 rounded-lg pl-8 text-sm py-2.5 px-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${errors.budget ? 'border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="0.00"
                          />
                          {errors.budget && <p className="text-xs text-red-500 mt-1">{errors.budget}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Team Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <UserGroupIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                      The Team
                    </h3>
                    <Button variant="outline" onClick={addCollaborator} className="text-xs px-2 py-1 h-8">
                      <PlusIcon className="w-3 h-3 mr-1" /> Add Role
                    </Button>
                  </div>

                  {/* Creator Role */}
                  <div className="bg-teal-50 dark:bg-teal-900/10 rounded-xl p-4 border border-teal-100 dark:border-teal-800/50">
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar
                        src={user?.profile_pic_signed || user?.profile_pic}
                        alt={`${user?.first_name || ''} ${user?.last_name || ''}`}
                        size="sm"
                        className="w-8 h-8"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">You (Creator)</p>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={formData.creator_role}
                      onChange={(e) => {
                        setFormData(f => ({ ...f, creator_role: e.target.value }));
                        if (errors.creator_role) setErrors(prev => ({ ...prev, creator_role: '' }));
                      }}
                      className={`w-full border-teal-200 dark:border-teal-800 rounded-lg text-sm focus:ring-teal-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-2.5 px-4 ${errors.creator_role ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Your Role (e.g. Creator)"
                    />
                    {errors.creator_role && <p className="text-xs text-red-500 mt-1">{errors.creator_role}</p>}
                    <textarea
                      value={formData.creator_insights}
                      onChange={(e) => setFormData(f => ({ ...f, creator_insights: e.target.value }))}
                      rows={2}
                      className="w-full border-teal-200 dark:border-teal-800 rounded-lg text-sm focus:ring-teal-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white mt-2 resize-none py-2 px-4"
                      placeholder="Your vision/notes..."
                    />
                  </div>

                  {/* Other Collaborators */}
                  <div className="space-y-3">
                    {collaborators.map((collaborator, idx) => (collaborator.id !== user?.id && (
                      <div key={collaborator.id} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700 shadow-sm relative group">
                        {!collaborator.is_locked && (
                          <button 
                            onClick={() => removeCollaborator(collaborator.id)}
                            className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg border-2 border-white dark:border-gray-800 transition-all hover:scale-110 z-20"
                            title="Remove member"
                          >
                            <XMarkIcon className="w-3.5 h-3.5 stroke-[2.5] text-white" />
                          </button>
                        )}
                        
                        <div className="space-y-3">
                          {/* Name First */}
                          <div className="relative flex items-center gap-2">
                             {collaborator.is_portal_user && collaborator.profile_pic && (
                                <Avatar
                                    src={collaborator.profile_pic_signed || collaborator.profile_pic}
                                    alt={collaborator.name}
                                    size="xs"
                                    className="w-8 h-8 flex-shrink-0"
                                />
                             )}
                            <div className="flex-1 relative" ref={(el) => {
                                if (el) suggestionsRefs.current[collaborator.id] = el;
                              }}>
                              <input
                                type="text"
                                value={collaborator.name}
                                onChange={(e) => {
                                  updateCollaborator(collaborator.id, 'name', e.target.value);
                                  updateCollaborator(collaborator.id, 'is_portal_user', false);
                                }}
                                onFocus={() => {
                                   if (collaborator.name.length >= 2) {
                                       setShowSuggestions(prev => ({ ...prev, [collaborator.id]: true }));
                                   }
                                }}
                                className={`w-full border-gray-200 dark:border-gray-700 rounded-lg text-sm py-2.5 px-4 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white ${errors[`collaborator_${collaborator.id}_name`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                                placeholder="Name or Search User..."
                              />
                              {errors[`collaborator_${collaborator.id}_name`] && (
                                <p className="text-xs text-red-500 mt-1">{errors[`collaborator_${collaborator.id}_name`]}</p>
                              )}
                              {/* Suggestions Dropdown */}
                              {showSuggestions[collaborator.id] && userSuggestions[collaborator.id]?.length > 0 && (
                                <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 max-h-48 overflow-y-auto">
                                  {userSuggestions[collaborator.id].map(u => (
                                    <button
                                      key={u.id}
                                      type="button"
                                      onClick={() => selectUser(collaborator.id, u)}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                    >
                                      <Avatar
                                        src={u.profile_pic_signed || u.profile_pic}
                                        alt={u.full_name}
                                        size="xs"
                                        className="w-6 h-6"
                                      />
                                      <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{u.full_name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{u.role}</p>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Role Second */}
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={collaborator.role}
                              onChange={(e) => updateCollaborator(collaborator.id, 'role', e.target.value)}
                              className={`w-full border-gray-200 dark:border-gray-700 rounded-lg text-sm py-2.5 px-4 font-medium placeholder:font-normal bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${errors[`collaborator_${collaborator.id}_role`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                              placeholder="Role (e.g. Model)"
                            />
                            {errors[`collaborator_${collaborator.id}_role`] && (
                              <p className="text-xs text-red-500">{errors[`collaborator_${collaborator.id}_role`]}</p>
                            )}
                          </div>

                          {!collaborator.is_portal_user && (
                            <input
                              type="email"
                              value={collaborator.email || ''}
                              onChange={(e) => updateCollaborator(collaborator.id, 'email', e.target.value)}
                              className="w-full border-gray-200 dark:border-gray-700 rounded-lg text-xs py-2 px-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              placeholder="Email (for invite)"
                            />
                          )}

                          {/* Rating & Review (Only for Portal Users) */}
                          {collaborator.is_portal_user && (
                            <div className="pt-2 border-t border-gray-100 dark:border-gray-700 mt-2">
                              {collaborator.is_existing_rating || existingReviews.some(r => r.reviewee_id === collaborator.user_id && r.booking_id === collaboration?.id) ? (
                                <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium py-1">
                                  <CheckCircleIcon className="w-4 h-4" /> Already Rated
                                  {/* {collaborator.is_existing_rating && (
                                    <span className="text-[10px] bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 px-1.5 py-0.5 rounded-full font-medium ml-2">
                                      Previous Rating
                                    </span>
                                  )} */}
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1">
                                      {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                          key={star}
                                          type="button"
                                          onClick={() => updateCollaborator(collaborator.id, 'rating', star)}
                                          className="focus:outline-none"
                                        >
                                          <StarIcon 
                                            className={`w-4 h-4 ${
                                              (collaborator.rating || 0) >= star ? 'text-yellow-400' : 'text-gray-200 dark:text-gray-700'
                                            }`} 
                                          />
                                        </button>
                                      ))}
                                      <span className="text-xs text-gray-500 ml-2">
                                        {collaborator.rating ? `${collaborator.rating}.0` : 'Rate'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {(collaborator.rating || 0) > 0 && (
                                    <textarea
                                      value={collaborator.review || ''}
                                      onChange={(e) => updateCollaborator(collaborator.id, 'review', e.target.value)}
                                      className="w-full border-gray-200 dark:border-gray-700 rounded-lg text-xs py-2 px-3 resize-none focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                      placeholder={`Write a review for ${collaborator.name.split(' ')[0]}...`}
                                      rows={2}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )))}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-4 py-3 md:px-8 md:py-5 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex flex-col gap-4">
             {loading && uploadProgress > 0 && (
                <div className="w-full">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Uploading Request...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
             <div className="flex items-center gap-3 ml-auto w-full justify-end">
                <Button
                variant="outline"
                onClick={handleCloseAttempt}
                disabled={loading}
                className="border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800"
                >
                Cancel
                </Button>
                <Button
                onClick={handleSaveClick}
                loading={loading}
                disabled={loading}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8"
                variant="primary"
                >
                {loading ? (uploadProgress >= 100 ? 'Processing...' : 'Uploading...') : submitButtonText}
                </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Draft Confirmation Modal */}
      {showDraftConfirm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={(e) => e.stopPropagation()}>
            <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm w-full shadow-xl border border-gray-100 dark:border-gray-800"
                onClick={e => e.stopPropagation()}
            >
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Save as Draft?</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    You have unsaved changes. Would you like to save them as a draft to finish later?
                </p>
                
                <div className="flex flex-col gap-3">
                    <Button 
                        onClick={handleSaveDraft}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white justify-center"
                        variant="primary"
                    >
                        Save Draft & Close
                    </Button>
                    <Button 
                        variant="outline"
                        onClick={handleDiscard}
                        className="w-full justify-center text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 border-red-200 dark:border-red-900/50"
                    >
                        Discard Changes
                    </Button>
                    <Button 
                        variant="ghost"
                        onClick={() => {
                            setShowDraftConfirm(false);
                            setPendingClose(false);
                        }}
                        className="w-full justify-center"
                    >
                        Keep Editing
                    </Button>
                </div>
            </motion.div>
        </div>
      )}

      {/* Reorder Confirmation Modal */}
      {showReorderConfirm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={(e) => e.stopPropagation()}>
            <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm w-full shadow-xl border border-gray-100 dark:border-gray-800"
                onClick={e => e.stopPropagation()}
            >
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Apply Reordering?</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    You have selected a new order for your media items but haven't clicked "Done Reordering". Would you like to apply this new order or discard it?
                </p>
                
                <div className="flex flex-col gap-3">
                    <Button 
                        onClick={() => {
                            const selectedItems = reorderSelection.map(id => galleryItems.find(item => item.id === id)).filter(Boolean) as GalleryItem[];
                            const unselectedItems = galleryItems.filter(item => !reorderSelection.includes(item.id));
                            const sortedItems = [...selectedItems, ...unselectedItems];

                            setGalleryItems(sortedItems);
                            setIsReordering(false);
                            setReorderSelection([]);
                            setShowReorderConfirm(false);
                            
                            handleSubmit(sortedItems);
                        }}
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white justify-center"
                        variant="primary"
                    >
                        Apply Order & Save
                    </Button>
                    <Button 
                        variant="outline"
                        onClick={() => {
                            cancelReordering();
                            setShowReorderConfirm(false);
                            handleSubmit();
                        }}
                        className="w-full justify-center border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                    >
                        Discard Order & Save
                    </Button>
                    <Button 
                        variant="ghost"
                        onClick={() => {
                            setShowReorderConfirm(false);
                        }}
                        className="w-full justify-center"
                    >
                        Cancel
                    </Button>
                </div>
            </motion.div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {viewingImageIndex !== null && (
        <ImageViewerModal
          images={galleryItems.filter(item => item.mediaType === 'image').map(item => item.url)}
          thumbnailImages={galleryItems.filter(item => item.mediaType === 'image').map(item => item.url)}
          mediumImages={galleryItems.filter(item => item.mediaType === 'image').map(item => item.url)}
          initialIndex={viewingImageIndex}
          onClose={() => setViewingImageIndex(null)}
        />
      )}

      </AnimatePresence>


        {/* [Subscription removed] UpgradePopup removed — platform is fully free. */}
      {/*<UpgradePopup*/}
      {/*  isOpen={upgradeConfig.isOpen}*/}
      {/*  onClose={() => setUpgradeConfig({ ...upgradeConfig, isOpen: false })}*/}
      {/*  featureName={upgradeConfig.featureName}*/}
      {/*  limit={upgradeConfig.limit}*/}
      {/*  tier={upgradeConfig.tier}*/}
      {/*/>*/}
    </>,
    document.getElementById('modal-root') || document.body
  );
};

export default CreateCollaborationModal;
